<!--
  Override of `@apexl/icarus-core`'s generic `Form.vue`.

  Two additions, both scoped as tightly as possible since this component
  renders every form in the app, not just the auth ones:

  1. `includeClearButton` / `clearButtonText` — real props `LoginFormBuilder`
     already sends (`user_login_include_clear_button` defaults `true`,
     text defaults "Cancel") that core's `Form.vue` never declared, so they
     silently fell through as unused fallthrough attributes and no button
     ever rendered. Implemented generically (any form carrying these props
     gets the button) rather than gated to auth classes, since no other
     current form sends them — this can only start a currently-nonexistent
     button rendering, never change existing behaviour.

  2. Inline error banner instead of a top-right toast, gated strictly to
     `props.classes` containing `login-form` or `forgotten-password-form`
     (the two auth FormBuilders' own class names) — the approved
     auth-screens design decision. Both endpoints report a declined
     attempt differently (login: HTTP 401; forgot-password: HTTP 200 with
     an embedded error message — see `api/vendor/apexl/io-user`'s
     `ValidateLoginAction` / `ForgotPasswordController`), and the shared
     `ApiResponseProcessor` interceptor (`@apexl/icarus-core`) already
     un-conditionally pushes that message onto the global toast store
     before this component ever sees the response — for 401s it also never
     rejects the promise (see `processError`), so there's no exception to
     catch. `clearMessages()` immediately after detecting an auth-form error
     removes the toast the interceptor just queued so it never has time to
     paint, before rendering the same content as an inline banner instead.
     `clearMessages()` wipes the *entire* toast store rather than just the
     one message — acceptable here because these are full-bleed, single-
     purpose pre-auth pages with no other concurrent traffic that could
     legitimately be toasting at the same moment. Every other form's
     classes never match, so this whole code path is dead weight for them
     and their existing behaviour (silent console.error on non-422 errors)
     is untouched.
-->
<template>
  <div :class="classes">
    <div v-if="pageHeading" class="ac-head-title">{{ pageHeading }}</div>
    <div v-if="title" class="formTitle" v-html="wrappedTitle"></div>
    <template v-for="{content, type} in messages">
      <div :class="type" class="generalFormMessages">
        <div class="message" v-html="content"></div>
      </div>
    </template>
    <div v-if="isAuthForm && bannerMessage" class="form-error-banner">
      <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="9" r="7" /><path d="M9 5.5v4M9 12.2v.1" /></svg>
      <p>{{ bannerMessage }}</p>
    </div>
    <FormKit
      :id="formId"
      v-model="model"
      :submit-label="submitText"
      type="form"
      :actions="!includeClearButton"
      @submit="handleSubmit"
    >
      <FormKitSchema
        :data="model"
        :schema="schema"
      />
      <component v-for="component in components" :is="component" :key="component.id"/>
      <div v-if="includeClearButton" class="btn-row">
        <button type="submit" class="form-submit-btn" :disabled="submitting">{{ submitText }}</button>
        <button
          type="button"
          class="form-clear-btn"
          @click="handleClear"
        >{{ clearButtonText }}</button>
      </div>
    </FormKit>
  </div>
</template>

<script setup>


import { computed, defineEmits, ref, onMounted, onUnmounted } from "vue";
import useApi from "@apexl/icarus-core/src/services/api";
import classesProp from '@apexl/icarus-core/src/components/_properties/classes';
import { FormKit, FormKitSchema, setErrors, reset } from "@formkit/vue";
import eventBus from "@apexl/icarus-core/src/services/eventBus";
import useMessageStore from "@apexl/icarus-core/src/store/useMessageStore";
import { useRoute } from "vue-router";

onMounted(() => {
  eventBus.$on('fieldUpdated', ({ fieldName, value }) => {
    if (model.value.hasOwnProperty(fieldName)) {
      model.value[fieldName] = value;
    }
  });
  eventBus.$on('fieldsCleared', ({ fieldNames }) => {
    fieldNames.forEach(fieldName => {
      if (model.value.hasOwnProperty(fieldName)) {
        model.value[fieldName] = null; // or set to default value as needed
      }
    });
  });
})

onUnmounted(() => {
  eventBus.$off('fieldUpdated');
  eventBus.$off('fieldsCleared');
})

const api = useApi();
const emit = defineEmits(['refresh']);

const messages = ref([]);
const props = defineProps({
  ...classesProp,
  formId: {
    type: String,
    required: true
  },
  components: {
    type: Array,
    required: true
  },
  submitText: {
    type: String,
    default: 'Submit'
  },
  removeSubmit: {
    type: Boolean,
    default: false
  },
  action: {
    type: String,
    required: true
  },
  method: {
    type: String,
    default: 'POST'
  },
  title: {
    type: String,
    default: ''
  },
  header: {
    type: Number,
    default: 5
  },
  schema: {
    type: Array,
    required: true,
  },
  values: {
    type: Object,
    default: () => {
    }
  },
  includeClearButton: {
    type: Boolean,
    default: false,
  },
  clearButtonText: {
    type: String,
    default: 'Clear',
  },
})

const inputErrors = ref({});
const submitting = ref(false);
const bannerMessage = ref('');

const model = ref(props.values);

const isAuthForm = computed(() => {
  const classList = typeof props.classes === 'string'
    ? props.classes.split(' ')
    : (Array.isArray(props.classes) ? props.classes : Object.keys(props.classes || {}));

  return classList.includes('login-form') || classList.includes('forgotten-password-form');
});

const wrappedTitle = computed(() => {
  return `<h${props.header}>${props.title}</h${props.header}>`;
})

// Auth-family bold heading (mockup states 1/3/6) — none of the three
// FormBuilders send this; their real `title` prop is the lighter
// subtitle line underneath (rendered above via `.formTitle`). Aspirational
// chrome, same category as the wordmark — same reasoning as
// `ContentTitle.vue`'s "Check your email" synthetic title. No-ops (empty
// string, nothing rendered) for every route/form this doesn't apply to.
const route = useRoute();
const pageHeading = computed(() => {
  if (route.path === '/login') return 'Sign in to Goshawk';
  if (route.path === '/forgot-password') return 'Reset your password';
  if (route.path.startsWith('/reset-password/')) return 'Choose a new password';
  return '';
});

// Auth endpoints report a declined attempt in two different shapes (see
// file header) — success responses are already unwrapped to bare `data` by
// the shared interceptor, auth-failure responses resolve to the raw axios
// response object un-unwrapped. Check both.
function extractErrorMessage(response) {
  const messagesBag = response?.messages || response?.data?.messages;
  if (!messagesBag) {
    return null;
  }
  const errorEntry = Object.values(messagesBag).find(m => m.type === 'error');
  return errorEntry?.content || null;
}

function showInlineError(message) {
  bannerMessage.value = message || 'Something went wrong. Please try again.';
  // Remove the toast the shared interceptor already queued for this exact
  // request before it has a chance to paint.
  useMessageStore().clearMessages();
}

function handleClear() {
  bannerMessage.value = '';
  // `reset(formId, props.values)` looked like the documented way to do
  // this, but `props.values` only carries the server-supplied initial
  // values (e.g. just `{redirect: '/'}` for login) — verified in DDEV that
  // FormKit's `reset()` does not blank out the fields that aren't in that
  // object. Rebuilding a blank object covering every current model key
  // (falling back to each field's own server-supplied initial, if any)
  // and driving both `model` (the `v-model`) and FormKit's own `reset()`
  // is what actually clears the visible inputs.
  const blank = {};
  Object.keys(model.value || {}).forEach((key) => {
    blank[key] = props.values?.[key] ?? '';
  });
  model.value = blank;
  reset(props.formId, blank);
}

async function handleSubmit(values) {
  inputErrors.values = {};
  bannerMessage.value = '';
  submitting.value = true;
  try {
    const formData = new FormData();
    Object.entries(values).filter(([field, value]) => {
      if (['__init', 'slots'].includes(field)) {
        return false;
      }

      return typeof value !== 'undefined';
    }).forEach(([field, value]) => {
      // Array values (checkbox groups, multi-selects, etc.) need to be
      // appended one entry at a time under `field[]` so PHP rebuilds them
      // as an array. A bare `formData.append(field, [1,2,3])` would
      // toString() to "1,2,3" and PHP would see a single string.
      if (!Array.isArray(value)) {
        formData.append(field, value);
      } else if (value.length > 0 && value[0]['file'] instanceof File ) {
        value.forEach((fileObj, index) => {
          formData.append(`${field}[${index}]`, fileObj.file);
        })
      }
      else value.forEach((item) => formData.append(`${field}[]`, item));
    });
    const response = await api.post(props.action, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    if (isAuthForm.value) {
      const errorMessage = extractErrorMessage(response);
      if (errorMessage) {
        showInlineError(errorMessage);
        return;
      }
    }

    //for docusign
    if(response.sign_url) {
      window.location.href = response.sign_url;
    }
  } catch (error) {
    const { status, data: failures } = error.response || {};
    if (status === 422) {
      setErrors(props.formId, failures);
    } else if (isAuthForm.value) {
      showInlineError(extractErrorMessage(error.response));
    } else {
      console.error(error)
    }
  } finally {
    submitting.value = false;
  }
}

</script>


<style lang="scss">
@use '@apexl/icarus-core/src/scss/variables/forms.scss' as forms;
@use '@apexl/icarus-core/src/scss/variables/colors.scss' as colors;
@use '@apexl/icarus-core/src/scss/variables/brands.scss' as brands;

.generalFormMessages {
  &.error {
    .message {
      margin-bottom: 20px;
      background: forms.$form--error--color;
      padding: 10px;
      color: brands.$brand--c1--hover-text;
    }
  }
}

.form-error-banner {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: rgba(209, 67, 67, .08);
  border: 1px solid rgba(209, 67, 67, .3);
  border-radius: var(--theme-radius, 6px);
  padding: 10px 12px;
  margin-bottom: 20px;

  svg {
    width: 14px;
    height: 14px;
    color: var(--theme-status-danger, #D14343);
    flex-shrink: 0;
    margin-top: 1px;
  }

  p {
    margin: 0;
    font-size: 12px;
    color: var(--theme-status-danger, #D14343);
    line-height: 1.5;
  }
}

form {
  --fk-max-width-input: none;
  --fk-font-weight-label: normal;
  // CSS custom properties don't evaluate Sass expressions in their value
  // unless you interpolate. Without `#{}`, the literal string
  // `colors.$c-white` ends up on the page.
  //--fk-color-input: #{colors.$c-white};
  //
  //color: colors.$c-white;

  label {
    font-weight: normal;
    text-align: left;
  }

  .formkit-messages {
    text-align: left;
  }

  .formkit-help {
    color: inherit;
    text-align: right;
  }

  .form-input--error {
    outline-color: forms.$form--error--color;
    outline-style: solid;
  }
}

.formTitle {
  color: #fff;
  margin-bottom: 2em;
}

.form-submit {
  &.loader-spinner {
    width: 20px;
  }
}

.hidden-field-wrapper {
  display: none;
}

input.form-input--error {
  border: 1px solid #ff0000;
}

span.error {
  display: block;
  margin-top: .25em;

  &.text-error {
    color: #ff0000;
  }
}


</style>
